/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import { NotificationSeverity, NotificationSource, type UINotification } from '@poweralpha/common';
import dayjs, { type Dayjs } from 'dayjs';
import type { NotificationCenterFilter } from './types';

export const sourceLabels: Record<NotificationSource, string> = {
  [NotificationSource.SIMULATION]: 'Simulations',
  [NotificationSource.BATCH]: 'Batches',
  [NotificationSource.BID_REQUEST]: 'Bid requests',
  [NotificationSource.AGENT]: 'Agent',
  [NotificationSource.RECORDS]: 'Records',
};

const sourceItemLabels: Record<NotificationSource, string> = {
  [NotificationSource.SIMULATION]: 'Simulation',
  [NotificationSource.BATCH]: 'Batch',
  [NotificationSource.BID_REQUEST]: 'Bid request',
  [NotificationSource.AGENT]: 'Agent',
  [NotificationSource.RECORDS]: 'Records',
};

export const severityLabels: Record<NotificationSeverity, string> = {
  [NotificationSeverity.ERROR]: 'Errors',
  [NotificationSeverity.WARNING]: 'Warnings',
  [NotificationSeverity.SUCCESS]: 'Success',
  [NotificationSeverity.INFO]: 'Info',
};

export const severityStatusLabels: Record<NotificationSeverity, string> = {
  [NotificationSeverity.ERROR]: 'Failed',
  [NotificationSeverity.WARNING]: 'Warning',
  [NotificationSeverity.SUCCESS]: 'Succeeded',
  [NotificationSeverity.INFO]: 'Info',
};

export const getFilterLabel = (filter: NotificationCenterFilter) => {
  switch (filter.kind) {
    case 'unread':
      return 'Unread';
    case 'severity':
      return severityLabels[filter.severity];
    case 'source':
      return sourceLabels[filter.source];
    default:
      return 'All notifications';
  }
};

export const getSourceItemLabel = (source: NotificationSource | undefined) =>
  sourceItemLabels[source ?? NotificationSource.SIMULATION];

// "Simulation · Box Canyon 1" — the source plus the first project, if any.
export const getNotificationContext = ({ source, details }: UINotification) => {
  const project = details?.find(({ label }) => label.startsWith('Project'))?.value;
  return [getSourceItemLabel(source), project].filter(Boolean).join(' · ');
};

// Logs are internal: notifications that point at a logs page (failed runs) or
// at the old notifications page open in the notification center instead.
export const opensInNotificationCenter = (link: string) =>
  link.startsWith('/notifications') || /\/logs(\/|\?|$)/.test(link);

export const getNotificationActionLabel = ({
  link,
  source,
}: Pick<UINotification, 'link' | 'source'>): string | null => {
  if (opensInNotificationCenter(link)) return null;
  switch (source) {
    case NotificationSource.BID_REQUEST:
      return 'Open bid request';
    case NotificationSource.AGENT:
      return 'Open conversation';
    case NotificationSource.RECORDS:
      return 'Open records';
    default:
      return 'View results';
  }
};

export interface NotificationDayGroup {
  key: string;
  label: string;
  notifications: Array<UINotification>;
}

const getDayLabel = (day: Dayjs, now: Dayjs) => {
  const daysAgo = now.startOf('day').diff(day, 'day');
  if (daysAgo === 0) return 'Today';
  if (daysAgo === 1) return 'Yesterday';
  if (daysAgo < 7) return day.format('dddd');
  return day.format(day.year() === now.year() ? 'MMM D' : 'MMM D, YYYY');
};

// Assumes notifications are already ordered newest first.
export const groupNotificationsByDay = (
  notifications: Array<UINotification>,
  now: Dayjs = dayjs(),
): Array<NotificationDayGroup> =>
  notifications.reduce<Array<NotificationDayGroup>>((groups, notification) => {
    const day = dayjs(notification.created_at).startOf('day');
    const key = day.format('YYYY-MM-DD');
    const last = groups.at(-1);
    if (last?.key === key) {
      last.notifications.push(notification);
    } else {
      groups.push({ key, label: getDayLabel(day, now), notifications: [notification] });
    }
    return groups;
  }, []);

// Short time for a row inside a day group: "now", "5m", "3h", otherwise the clock time.
export const formatNotificationTime = (createdAt: Date | string, now: Dayjs = dayjs()) => {
  const created = dayjs(createdAt);
  const minutes = now.diff(created, 'minute');
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  if (created.isSame(now, 'day')) return `${now.diff(created, 'hour')}h`;
  return created.format('h:mm A');
};
